/**
 * Workflow Engine API Service
 */

import apiClient from './client';

// =============================================================================
// TEMPLATES (Admin)
// =============================================================================

export const listTemplates = async ({ includeInactive = false, entityType = null } = {}) => {
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');
  if (entityType) params.append('entity_type', entityType);
  
  const { data } = await apiClient.get(`/api/v1/workflows/templates?${params}`);
  return data;
};

export const getTemplate = async (templateId) => {
  const { data } = await apiClient.get(`/api/v1/workflows/templates/${templateId}`);
  return data;
};

export const createTemplate = async (templateData) => {
  const { data } = await apiClient.post('/api/v1/workflows/templates', templateData);
  return data;
};

export const updateTemplate = async (templateId, templateData) => {
  const { data } = await apiClient.put(`/api/v1/workflows/templates/${templateId}`, templateData);
  return data;
};

export const deleteTemplate = async (templateId) => {
  const { data } = await apiClient.delete(`/api/v1/workflows/templates/${templateId}`);
  return data;
};

// =============================================================================
// STEPS
// =============================================================================

export const addStep = async (templateId, stepData) => {
  const { data } = await apiClient.post(`/api/v1/workflows/templates/${templateId}/steps`, stepData);
  return data;
};

export const updateStep = async (stepId, stepData) => {
  const { data } = await apiClient.put(`/api/v1/workflows/steps/${stepId}`, stepData);
  return data;
};

export const deleteStep = async (stepId) => {
  const { data } = await apiClient.delete(`/api/v1/workflows/steps/${stepId}`);
  return data;
};

export const reorderSteps = async (templateId, stepOrders) => {
  const { data } = await apiClient.put(`/api/v1/workflows/templates/${templateId}/steps/reorder`, {
    step_orders: stepOrders
  });
  return data;
};

// =============================================================================
// FIELDS
// =============================================================================

export const addField = async (stepId, fieldData) => {
  const { data } = await apiClient.post(`/api/v1/workflows/steps/${stepId}/fields`, fieldData);
  return data;
};

export const updateField = async (fieldId, fieldData) => {
  const { data } = await apiClient.put(`/api/v1/workflows/fields/${fieldId}`, fieldData);
  return data;
};

export const deleteField = async (fieldId) => {
  const { data } = await apiClient.delete(`/api/v1/workflows/fields/${fieldId}`);
  return data;
};

// =============================================================================
// TRANSITIONS
// =============================================================================

export const addTransition = async (templateId, transitionData) => {
  const { data } = await apiClient.post(`/api/v1/workflows/templates/${templateId}/transitions`, transitionData);
  return data;
};

export const deleteTransition = async (transitionId) => {
  const { data } = await apiClient.delete(`/api/v1/workflows/transitions/${transitionId}`);
  return data;
};

// =============================================================================
// INSTANCES (User-facing)
// =============================================================================

export const startWorkflow = async (instanceData) => {
  const { data } = await apiClient.post('/api/v1/workflows/instances', instanceData);
  return data;
};

export const listInstances = async ({ entityType = null, entityId = null, status = null, assignedToMe = false } = {}) => {
  const params = new URLSearchParams();
  if (entityType) params.append('entity_type', entityType);
  if (entityId) params.append('entity_id', entityId);
  if (status) params.append('status', status);
  if (assignedToMe) params.append('assigned_to_me', 'true');
  
  const { data } = await apiClient.get(`/api/v1/workflows/instances?${params}`);
  return data;
};

export const getInstance = async (instanceId) => {
  const { data } = await apiClient.get(`/api/v1/workflows/instances/${instanceId}`);
  return data;
};

export const getMyTasks = async (status = null) => {
  const params = status ? `?status=${status}` : '';
  const { data } = await apiClient.get(`/api/v1/workflows/my-tasks${params}`);
  return data;
};

export const completeStep = async (stepInstanceId, completionData) => {
  const { data } = await apiClient.post(`/api/v1/workflows/step-instances/${stepInstanceId}/complete`, completionData);
  return data;
};

export const updateStepFields = async (stepInstanceId, fieldValues) => {
  const { data } = await apiClient.put(`/api/v1/workflows/step-instances/${stepInstanceId}/fields`, {
    field_values: fieldValues
  });
  return data;
};

export const cancelWorkflow = async (instanceId, reason = null) => {
  const params = reason ? `?reason=${encodeURIComponent(reason)}` : '';
  const { data } = await apiClient.post(`/api/v1/workflows/instances/${instanceId}/cancel${params}`);
  return data;
};

// =============================================================================
// ACTIVITY LOG
// =============================================================================

export const getActivityLog = async (instanceId, limit = 50) => {
  const { data } = await apiClient.get(`/api/v1/workflows/instances/${instanceId}/activity?limit=${limit}`);
  return data;
};

// =============================================================================
// ENTITY-SPECIFIC
// =============================================================================

export const getEntityWorkflows = async (entityType, entityId, status = null) => {
  const params = status ? `?status=${status}` : '';
  const { data } = await apiClient.get(`/api/v1/workflows/entities/${entityType}/${entityId}/workflows${params}`);
  return data;
};

// =============================================================================
// ANALYTICS
// =============================================================================

/**
 * Get workflow analytics summary
 */
export const getAnalyticsSummary = async (days = 30) => {
  const { data } = await apiClient.get(`/api/v1/workflows/analytics/summary?days=${days}`);
  return data;
};

/**
 * Get analytics by template
 */
export const getAnalyticsByTemplate = async (days = 30) => {
  const { data } = await apiClient.get(`/api/v1/workflows/analytics/by-template?days=${days}`);
  return data;
};

/**
 * Get step bottlenecks
 */
export const getBottlenecks = async (days = 30, limit = 10) => {
  const { data } = await apiClient.get(`/api/v1/workflows/analytics/bottlenecks?days=${days}&limit=${limit}`);
  return data;
};

/**
 * Get user workload distribution
 */
export const getUserWorkload = async () => {
  const { data } = await apiClient.get('/api/v1/workflows/analytics/user-workload');
  return data;
};

/**
 * Get workflow trends
 */
export const getWorkflowTrends = async (days = 30) => {
  const { data } = await apiClient.get(`/api/v1/workflows/analytics/trends?days=${days}`);
  return data;
};

/**
 * Get recent workflow activity
 */
export const getRecentActivity = async (limit = 20) => {
  const { data } = await apiClient.get(`/api/v1/workflows/analytics/recent-activity?limit=${limit}`);
  return data;
};


// =============================================================================
// STALE WORKFLOWS
// =============================================================================

/**
 * Get stale workflows that haven't been updated within their threshold
 */
export const getStaleWorkflows = async (thresholdDays = null) => {
  const params = thresholdDays ? `?threshold_days=${thresholdDays}` : '';
  const { data } = await apiClient.get(`/api/v1/workflows/stale${params}`);
  return data;
};

/**
 * Get count of stale workflows for badges/alerts
 */
export const getStaleWorkflowCount = async () => {
  const { data } = await apiClient.get('/api/v1/workflows/stale/count');
  return data;
};

/**
 * Update stale threshold for a workflow template
 */
export const updateStaleThreshold = async (templateId, thresholdDays) => {
  const { data } = await apiClient.put(`/api/v1/workflows/templates/${templateId}/stale-threshold`, {
    threshold_days: thresholdDays
  });
  return data;
};
